/**
 * Keelan Scott - Global JavaScript
 * Core functionality for the Shopify theme
 */

// Debounce utility
function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Throttle utility
function throttle(fn, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Format money helper
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.theme?.moneyFormat || '${{amount}}';

  function addCommas(n) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  let value = '';
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = addCommas((cents / 100).toFixed(2));
      break;
    case 'amount_no_decimals':
      value = addCommas(Math.floor(cents / 100));
      break;
    case 'amount_with_comma_separator':
      value = (cents / 100).toFixed(2).replace('.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = addCommas(Math.floor(cents / 100)).replace(',', '.');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

// Fetch API wrapper with error handling
async function fetchJSON(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Intersection Observer for lazy loading and animations
function createObserver(callback, options = {}) {
  const defaultOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  };

  return new IntersectionObserver(callback, { ...defaultOptions, ...options });
}

// Initialize lazy load animations
function initLazyAnimations() {
  const animatedElements = document.querySelectorAll('[data-animate]');

  if (animatedElements.length === 0) return;

  const observer = createObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = entry.target;
        const animation = el.dataset.animate || 'fade-in';
        el.classList.add(`animate-${animation}`);
        observer.unobserve(el);
      }
    });
  });

  animatedElements.forEach((el) => observer.observe(el));
}

// Smooth scroll to element
function scrollToElement(selector, offset = 0) {
  const element = document.querySelector(selector);
  if (!element) return;

  const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - headerHeight - offset;

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth',
  });
}

// Handle anchor links with smooth scroll
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      e.preventDefault();
      scrollToElement(href);
    });
  });
}

// Scroll to a hash target on page load (e.g. arriving via /#story from another page)
function handleHashOnLoad() {
  const hash = window.location.hash;
  if (!hash || hash === '#') return;
  if (!document.querySelector(hash)) return;

  // Defer so layout/fonts settle and the header offset is correct.
  window.setTimeout(() => scrollToElement(hash), 100);
}

// Header scroll behavior
function initHeaderScroll() {
  const header = document.querySelector('.header');
  if (!header) return;

  let lastScroll = 0;

  window.addEventListener(
    'scroll',
    throttle(() => {
      const currentScroll = window.pageYOffset;

      if (currentScroll <= 0) {
        header.classList.remove('header--scrolled', 'header--hidden');
        return;
      }

      if (currentScroll > lastScroll && currentScroll > 100) {
        // Scrolling down
        header.classList.add('header--hidden');
      } else {
        // Scrolling up
        header.classList.remove('header--hidden');
        header.classList.add('header--scrolled');
      }

      lastScroll = currentScroll;
    }, 100)
  );
}

// Product image zoom on hover
function initImageZoom() {
  const zoomContainers = document.querySelectorAll('[data-image-zoom]');

  zoomContainers.forEach((container) => {
    const image = container.querySelector('img');
    if (!image) return;

    container.addEventListener('mousemove', (e) => {
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      image.style.transformOrigin = `${x}% ${y}%`;
    });

    container.addEventListener('mouseenter', () => {
      image.style.transform = 'scale(1.5)';
    });

    container.addEventListener('mouseleave', () => {
      image.style.transform = 'scale(1)';
      image.style.transformOrigin = 'center center';
    });
  });
}

// Form validation helper
function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Newsletter form handling
function initNewsletterForms() {
  const forms = document.querySelectorAll('.newsletter-form, .klaviyo-form');

  forms.forEach((form) => {
    form.addEventListener('submit', async function (e) {
      const emailInput = this.querySelector('input[type="email"]');
      const successMessage = document.querySelector('[data-newsletter-success]');

      if (!validateEmail(emailInput.value)) {
        e.preventDefault();
        emailInput.classList.add('input--error');
        return;
      }

      emailInput.classList.remove('input--error');

      // If using native Shopify form, let it submit normally
      // If using Klaviyo, the form will submit to Klaviyo's endpoint

      if (successMessage) {
        // For visual feedback after form submission
        setTimeout(() => {
          successMessage.classList.add('is-visible');
        }, 500);
      }
    });
  });
}

// Tabs functionality
function initTabs() {
  const tabContainers = document.querySelectorAll('[data-tabs]');

  tabContainers.forEach((container) => {
    const triggers = container.querySelectorAll('[data-tab-trigger]');
    const panels = container.querySelectorAll('[data-tab-panel]');

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const targetId = trigger.dataset.tabTrigger;

        // Update triggers
        triggers.forEach((t) => t.classList.remove('is-active'));
        trigger.classList.add('is-active');

        // Update panels
        panels.forEach((panel) => {
          panel.classList.remove('is-active');
          if (panel.dataset.tabPanel === targetId) {
            panel.classList.add('is-active');
          }
        });
      });
    });
  });
}

// Initialize all functionality
document.addEventListener('DOMContentLoaded', () => {
  initLazyAnimations();
  initSmoothScroll();
  handleHashOnLoad();
  initHeaderScroll();
  initImageZoom();
  initNewsletterForms();
  initTabs();
});

// Export utilities for use in other scripts
window.theme = window.theme || {};
window.theme.utils = {
  debounce,
  throttle,
  formatMoney,
  fetchJSON,
  scrollToElement,
  validateEmail,
};
